import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import {
  ROW_MARKER_COLORS,
  clampRowMarkerColor,
  type RowMarker,
} from "../../../../src/lib/audio";
import { useStoreSelector } from "../../../../src/lib/store";
import { getLabActions } from "../labActions";
import { patchUi, uiStore, type RowLabelEditState } from "../store/uiStore";

const PANEL_WIDTH = 260;

/**
 * Floating editor for a tracker row label + tint.
 */
export function RowLabelModal() {
  const edit = useStoreSelector(uiStore, s => s.rowLabelEdit);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [label, setLabel] = useState("");
  const [color, setColor] = useState(0);
  const draftRef = useRef({ label: "", color: 0, step: 0 });

  useEffect(() => {
    if (!edit) {
      return;
    }
    setLabel(edit.label);
    setColor(clampRowMarkerColor(edit.color));
    draftRef.current = {
      label: edit.label,
      color: clampRowMarkerColor(edit.color),
      step: edit.step,
    };
    const id = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(id);
  }, [edit]);

  useEffect(() => {
    draftRef.current.label = label;
    draftRef.current.color = color;
  }, [label, color]);

  useEffect(() => {
    if (!edit) {
      return;
    }
    function close(): void {
      patchUi({ rowLabelEdit: null });
      getLabActions()?.focusTracker?.();
    }
    function commit(marker: RowMarker | null): void {
      const step = draftRef.current.step;
      patchUi({ rowLabelEdit: null });
      getLabActions()?.commitRowLabel?.(step, marker);
    }
    function commitFromInput(): void {
      const trimmed = draftRef.current.label.trim();
      if (!trimmed) {
        commit(null);
        return;
      }
      commit({
        label: trimmed,
        color: clampRowMarkerColor(draftRef.current.color),
      });
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    }
    function onPointerDown(event: PointerEvent): void {
      const panel = panelRef.current;
      if (event.target instanceof Node && panel?.contains(event.target)) {
        return;
      }
      commitFromInput();
    }
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [edit]);

  if (!edit) {
    return null;
  }

  function close(): void {
    patchUi({ rowLabelEdit: null });
    getLabActions()?.focusTracker?.();
  }

  function commit(marker: RowMarker | null): void {
    const step = edit.step;
    patchUi({ rowLabelEdit: null });
    getLabActions()?.commitRowLabel?.(step, marker);
  }

  function commitFromInput(): void {
    const trimmed = label.trim();
    if (!trimmed) {
      commit(null);
      return;
    }
    commit({ label: trimmed, color: clampRowMarkerColor(color) });
  }

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    commitFromInput();
  }

  function onInputKeyDown(event: ReactKeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Enter") {
      event.preventDefault();
      commitFromInput();
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
  }

  const pos = positionFor(edit.anchor);

  return createPortal(
    <form
      ref={panelRef}
      className="row-label-modal fixed z-[55] flex flex-col gap-[0.55rem] rounded-lg border border-lab-line bg-lab-panel px-[0.7rem] pt-[0.65rem] pb-[0.7rem] text-[12px] text-slate-300 shadow-[0_18px_40px_rgba(0,0,0,0.45)]"
      role="dialog"
      aria-label="Row label"
      style={{ left: pos.left, top: pos.top, width: PANEL_WIDTH }}
      onSubmit={onSubmit}
    >
      <div className="text-[11px] font-semibold tracking-[0.04em] text-slate-100 uppercase">
        {`Row ${String(edit.step).padStart(2, "0")} label`}
      </div>
      <input
        ref={inputRef}
        type="text"
        className="w-full rounded border border-lab-line bg-lab-bg px-2 py-[0.4rem] text-[13px] text-lab-text outline-none focus:border-lab-accent focus:shadow-[0_0_0_2px_rgba(61,139,253,0.25)]"
        maxLength={48}
        placeholder="Label (empty = remove)"
        spellCheck={false}
        value={label}
        onChange={event => setLabel(event.target.value)}
        onKeyDown={onInputKeyDown}
      />
      <div className="flex flex-wrap gap-[0.35rem]" role="listbox" aria-label="Row color">
        {ROW_MARKER_COLORS.map((hex, i) => (
          <button
            key={hex}
            type="button"
            className={
              color === i
                ? "size-[1.45rem] rounded-full border-2 border-slate-50 p-0 shadow-[0_0_0_2px_rgba(15,18,22,0.9),inset_0_0_0_1px_rgba(0,0,0,0.25)]"
                : "size-[1.45rem] rounded-full border-2 border-transparent p-0 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.35)] hover:brightness-[1.08]"
            }
            style={{ background: hex }}
            title={`Color ${i + 1}`}
            aria-label={`Color ${i + 1}`}
            aria-selected={color === i}
            onClick={() => setColor(clampRowMarkerColor(i))}
          />
        ))}
      </div>
      <div className="flex justify-end gap-[0.35rem]">
        <button
          type="button"
          className="px-[0.55rem] py-[0.3rem] text-[11px]"
          onClick={() => commit(null)}
        >
          Clear
        </button>
        <button
          type="submit"
          className="border-lab-accent bg-[rgba(61,139,253,0.22)] px-[0.55rem] py-[0.3rem] text-[11px] text-white"
        >
          Save
        </button>
      </div>
    </form>,
    document.body,
  );
}

function positionFor(anchor: RowLabelEditState["anchor"]): { left: number; top: number } {
  return {
    left: Math.min(window.innerWidth - PANEL_WIDTH - 8, Math.max(8, anchor.left)),
    top: Math.min(window.innerHeight - 180, Math.max(8, anchor.bottom + 6)),
  };
}
